/**
 * Data Source Logger - Tracks demo vs production data access for audit compliance
 * 
 * CRITICAL: This logger ensures strict separation between demo and real user data.
 * All analytics hooks and data-fetching components must use this for audit trails.
 */

import { dataSourceLogger as dsLogger } from '@/lib/logger';

type DataSource = 'demo' | 'production';

interface DataSourceLogEntry {
  timestamp: string;
  source: DataSource;
  context: string;
  userId?: string | null;
  details?: Record<string, unknown>;
}

// Keep last 100 entries for debugging
const logBuffer: DataSourceLogEntry[] = [];
const MAX_BUFFER_SIZE = 100;

/**
 * Log data source access for compliance auditing
 */
export const logDataSource = (
  source: DataSource,
  context: string,
  details?: Record<string, unknown>,
  userId?: string | null
): void => {
  const entry: DataSourceLogEntry = {
    timestamp: new Date().toISOString(),
    source,
    context,
    userId,
    details,
  };

  // Structured log (respects production log level filter)
  const sourceLabel = source === 'demo' ? 'DEMO' : 'PRODUCTION';
  dsLogger.debug(`${sourceLabel} | ${context}`, details || '');

  // Add to buffer
  logBuffer.push(entry);
  if (logBuffer.length > MAX_BUFFER_SIZE) {
    logBuffer.shift();
  }
};

/**
 * Log when demo data is accessed - for audit warnings
 */
export const logDemoAccess = (context: string, userId?: string | null): void => {
  dsLogger.warn(`Demo data accessed: ${context}`, { userId });
  logDataSource('demo', context, { warning: 'Demo data rendered' }, userId);
};

/**
 * Log when production data is empty - for honest empty states
 */
export const logProductionEmpty = (context: string, userId?: string | null): void => {
  dsLogger.debug(`No real data: ${context}`, { userId });
  logDataSource('production', context, { isEmpty: true }, userId);
};

/**
 * Log when real production data is successfully loaded
 */
export const logProductionData = (
  context: string, 
  recordCount: number, 
  userId?: string | null
): void => {
  dsLogger.info(`Real data loaded: ${context} (${recordCount} records)`, { userId });
  logDataSource('production', context, { recordCount, hasData: recordCount > 0 }, userId);
};

/**
 * Get recent data source logs for debugging
 */
export const getDataSourceLogs = (): DataSourceLogEntry[] => {
  return [...logBuffer];
};

/**
 * Clear data source logs
 */
export const clearDataSourceLogs = (): void => {
  logBuffer.length = 0;
};

/**
 * Check if currently in demo mode based on localStorage
 */
export const isInDemoMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('demo_mode') === 'true';
};

/**
 * Get summary of recent data source access for admin auditing
 */
export const getDataSourceSummary = (): {
  totalLogs: number;
  demoAccess: number;
  productionAccess: number;
  recentContexts: string[];
} => {
  const demoAccess = logBuffer.filter(l => l.source === 'demo').length;
  const productionAccess = logBuffer.filter(l => l.source === 'production').length;
  const recentContexts = [...new Set(logBuffer.slice(-10).map(l => l.context))];
  
  return {
    totalLogs: logBuffer.length,
    demoAccess,
    productionAccess,
    recentContexts,
  };
};
